/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { Plus, MapPin, Weight, Thermometer, Eye, Syringe, Stethoscope } from 'lucide-vue-next'
import { useGoatStore } from '@/stores/goat.store.js'
import { useGroupStore } from '@/stores/group.store.js'
import { useWeightStore } from '@/stores/weight.store.js'
import { useTemperatureStore } from '@/stores/temperature.store.js'
import { useFamachaStore } from '@/stores/famacha.store.js'
import { useHealthStore } from '@/stores/health.store.js'

/**
 * Composable to fetch and aggregate recent activities from various stores.
 * Used in RecentActivitiesModule and ActivitiesView.
 */

export function useActivities() {
  const { t } = useI18n()
  // Initialize stores
  const goatStore = useGoatStore()
  const { goats } = storeToRefs(goatStore)
  const groupStore = useGroupStore()
  const { groups } = storeToRefs(groupStore)
  const weightStore = useWeightStore()
  const { allWeights } = storeToRefs(weightStore)
  const temperatureStore = useTemperatureStore()
  const { temperatures } = storeToRefs(temperatureStore)
  const famachaStore = useFamachaStore()
  const { famachaChecks } = storeToRefs(famachaStore)
  const healthStore = useHealthStore()
  const { healthRecords } = storeToRefs(healthStore)
  
  const loading = ref(false)


  // Formats a date into a relative time string 
  function formatTimeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    if (seconds < 60) return t('common.timeAgo.justNow')
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return t('common.timeAgo.minutes', { n: minutes })
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return t('common.timeAgo.hours', { n: hours })
    const days = Math.floor(hours / 24)
    return t('common.timeAgo.days', { n: days })
  }

  // Refreshes data in all stores to ensure activities are up-to-date.
  async function refresh() {
    loading.value = true
    try {
      await Promise.all([
        goatStore.fetchGoats(),
        groupStore.fetchGroups(),
        weightStore.fetchAllWeights(),
        temperatureStore.fetchAllTemperatures(),
        famachaStore.fetchAllFamachaChecks(),
        healthStore.fetchHealthRecords(),
      ])
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      loading.value = false
    }
  }


  // Helper to get a display name for a goat by ID.
  function getGoatName(id) {
    const goat = goats.value.find(g => g.id === id)
    return goat ? (goat.name || goat.eartag || 'ID: ' + id) : t('views.selectedGoat.notFound')
  }

  /**
   * Computed property that aggregates all activities from different stores,
   * formats them into a unified structure, sorts by date, and adds time strings.
   */
  const activities = computed(() => {
      const _goats = goats.value || []
      const _groups = groups.value || []
      const _weights = allWeights.value || []
      const _temperatures = temperatures.value || []
      const _famachas = famachaChecks.value || []
      const _healthRecords = healthRecords.value || []

      // Map each data type to a unified activity object 
      const allActivities = [
        ..._goats.map(goat => ({
          type: 'goat',
          id: goat.id,
          title: `${t('views.home.activity.new_goat')}: ${goat.eartag || goat.name || (goat.dateOfBirth ? `${goat.dateOfBirth} (DOB)` : 'ID: ' + goat.id)}`,
          timestamp: new Date(goat.createdAt || Date.now()),
          icon: Plus,
          color: 'bg-blue-100 text-blue-900 dark:bg-blue-900/50 dark:text-blue-300'
        })),
        ..._groups.map(group => ({
          type: 'group',
          id: group.id,
          title: `${t('views.home.activity.new_group')}: ${group.name}${group.location ? ` (${group.location})` : ''}`,
          timestamp: new Date(group.createdAt || Date.now()),
          icon: MapPin,
          color: 'bg-purple-100 text-purple-900 dark:bg-purple-900/50 dark:text-purple-300'
        })),
        ..._weights.map(w => ({
          type: 'weight',
          id: w.id,
          goatId: w.goatId,
          title: `${t('views.session.options.weight')}: ${w.value}kg (${getGoatName(w.goatId)})`,
          timestamp: new Date(w.measuredAt || w.date || w.createdAt || Date.now()),
          icon: Weight,
          color: 'bg-green-100 text-green-900 dark:bg-green-900/50 dark:text-green-300'
        })),
        ..._temperatures.map(temp => ({
          type: 'temperature',
          id: temp.id,
          goatId: temp.goatId,
          title: `${t('views.session.options.temperature')}: ${temp.value}°C (${getGoatName(temp.goatId)})`,
          timestamp: new Date(temp.measuredAt || temp.date || temp.createdAt || Date.now()),
          icon: Thermometer,
          color: 'bg-red-100 text-red-900 dark:bg-red-900/50 dark:text-red-300'
        })),
        ..._famachas.map(f => ({
          type: 'famacha',
          id: f.id,
          goatId: f.goatId,
          title: `${t('views.session.options.famacha')}: ${f.score} (${getGoatName(f.goatId)})`,
          timestamp: new Date(f.measuredAt || f.date || f.createdAt || Date.now()),
          icon: Eye,
          color: 'bg-orange-100 text-orange-900 dark:bg-orange-900/50 dark:text-orange-300'
        })),
        ..._healthRecords.filter(r => ['treatment', 'vaccination'].includes(r.type)).map(treatment => ({
          type: 'treatment',
          id: treatment.id,
          goatId: treatment.goatId,
          title: `${t('views.session.options.treatment')}: ${treatment.title || treatment.description} (${getGoatName(treatment.goatId)})`,
          timestamp: new Date(treatment.measuredAt || treatment.date || treatment.createdAt || Date.now()),
          icon: Syringe,
          color: 'bg-pink-100 text-pink-900 dark:bg-pink-900/50 dark:text-pink-300'
        })),
        ..._healthRecords.filter(r => ['wound', 'disease', 'injury'].includes(r.type)).map(w => ({
          type: 'wound',
          id: w.id,
          goatId: w.goatId,
          title: `${t('views.session.options.wound')}: ${w.title || w.description} (${getGoatName(w.goatId)})`,
          timestamp: new Date(w.measuredAt || w.date || w.createdAt || Date.now()),
          icon: Stethoscope,
          color: 'bg-rose-100 text-rose-900 dark:bg-rose-900/50 dark:text-rose-300'
        }))
      ]

      // Sort by timestamp descending newest first and add formatted time string
      return allActivities
        .sort((a, b) => b.timestamp - a.timestamp)
        .map(item => ({
        ...item,
        time: formatTimeAgo(item.timestamp)
      }))
  })

  return {
    loading,
    refresh,
    activities
  }
}